import React, { useState, useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import {
  UserIcon,
  BellIcon,
  LockClosedIcon,
  ShieldCheckIcon,
  DocumentTextIcon,
  CalendarDaysIcon,
} from "@heroicons/react/24/outline";

const PersonalAdminAsideBar = ({ setShowAsideBar }) => {
  const location = useLocation();
  const [activeLink, setActiveLink] = useState("/admin-dashboard/settings");
  const [showUserProfileLinks, setShowUserProfileLinks] = useState(true);
  const [showPreferencesLinks, setShowPreferencesLinks] = useState(false);

  useEffect(() => {
    setActiveLink(location.pathname);
  }, [location.pathname]);

  const handleProfileOptimizationClick = () => {
    setShowUserProfileLinks(true);
    setShowPreferencesLinks(false);
  };

  const handleCalendarClick = () => {
    setShowUserProfileLinks(false);
    setShowPreferencesLinks(true);
    setActiveLink("/admin-dashboard/settings/calendar");
  };

  const handleSidebarTabs = () => {
    setShowAsideBar(false);
    setShowUserProfileLinks(false);
    setShowPreferencesLinks(false);
  };

  return (
    <div className="py-6 px-5 3xl:px-[30px] rounded-md border border-solid border-[#EBEFF3] bg-white">
      <div className="pb-5 mb-5 border-b border-solid border-[#EBEFF3]">
        <h6 className="text-xs font-bold leading-[160%] uppercase text-[#485868] mb-[14px]">
          Profile Settings
        </h6>
        <ul>
          <li className="mb-[14px]">
            <Link
              to="/admin-dashboard/settings"
              className={`text-base font-bold leading-[160%] text-customGray flex items-center gap-2.5 -tracking-[0.3px] ${
                activeLink === "/admin-dashboard/settings" ? "text-primary" : ""
              }`}
              onClick={handleProfileOptimizationClick}
            >
              <UserIcon
                className={`w-6 h-6 text-customGray ${
                  activeLink === "/admin-dashboard/settings"
                    ? "text-primary"
                    : ""
                }`}
              />
              Profile Optimization
            </Link>
          </li>
          <li>
            <Link
              className={`text-base font-bold leading-[160%] text-customGray flex items-center gap-2.5 ${
                activeLink === "/admin-dashboard/settings/calendar"
                  ? "text-primary"
                  : ""
              }`}
              onClick={handleCalendarClick}
              to="/admin-dashboard/settings/calendar"
            >
              <CalendarDaysIcon
                className={`w-6 h-6 text-customGray ${
                  activeLink === "/admin-dashboard/settings/calendar"
                    ? "text-primary"
                    : ""
                }`}
              />
              Calendar
            </Link>
          </li>
        </ul>
      </div>
      <div className="pb-5 mb-5 border-b border-solid border-[#EBEFF3]">
        <h6 className="text-xs font-bold leading-[160%] uppercase text-[#485868] mb-[14px]">
          Team
        </h6>
        <ul>
          <li>
            <Link
              className={`text-base font-bold leading-[160%] text-customGray flex items-center gap-2.5 mb-[14px] ${
                activeLink === "/admin-dashboard/settings/team-group"
                  ? "text-primary"
                  : ""
              }`}
              onClick={handleSidebarTabs}
              to="/admin-dashboard/settings/team-group"
            >
              <span
                className={`${
                  activeLink === "/admin-dashboard/settings/team-group"
                    ? "text-primary stroke-primary"
                    : ""
                }`}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="25"
                  viewBox="0 0 24 25"
                  fill="none"
                >
                  <g clipPath="url(#clip0_1034_41958)">
                    <path
                      d="M3 21.2762H21"
                      stroke={`${
                        activeLink === "/admin-dashboard/settings/team-group"
                          ? "stroke-primary"
                          : "#1C1C1C"
                      }`}
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M5 21.2762V7.27625L13 3.27625V21.2762"
                      stroke={`${
                        activeLink === "/admin-dashboard/settings/team-group"
                          ? "stroke-primary"
                          : "#1C1C1C"
                      }`}
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M19 21.2762V11.2762L13 7.27625"
                      stroke={`${
                        activeLink === "/admin-dashboard/settings/team-group"
                          ? "stroke-primary"
                          : "#1C1C1C"
                      }`}
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M9 9.27625V9.28625"
                      stroke={`${
                        activeLink === "/admin-dashboard/settings/team-group"
                          ? "stroke-primary"
                          : "#1C1C1C"
                      }`}
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M9 12.2762V12.2862"
                      stroke={`${
                        activeLink === "/admin-dashboard/settings/team-group"
                          ? "stroke-primary"
                          : "#1C1C1C"
                      }`}
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M9 15.2762V15.2862"
                      stroke={`${
                        activeLink === "/admin-dashboard/settings/team-group"
                          ? "stroke-primary"
                          : "#1C1C1C"
                      }`}
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M9 18.2762V18.2862"
                      stroke={`${
                        activeLink === "/admin-dashboard/settings/team-group"
                          ? "stroke-primary"
                          : "#1C1C1C"
                      }`}
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_1034_41958">
                      <rect
                        width="24"
                        height="24"
                        fill="white"
                        transform="translate(0 0.276245)"
                      />
                    </clipPath>
                  </defs>
                </svg>
              </span>
              Team Group Settings
            </Link>
          </li>
          <li>
            <Link
              className={`text-base font-bold leading-[160%] text-customGray flex items-center gap-2.5 ${
                activeLink === "/admin-dashboard/settings/team-member"
                  ? "text-primary"
                  : ""
              }`}
              onClick={handleSidebarTabs}
              to="/admin-dashboard/settings/team-member"
            >
              <CalendarDaysIcon
                className={`w-6 h-6 text-customGray ${
                  activeLink === "/admin-dashboard/settings/team-member"
                    ? "text-primary"
                    : ""
                }`}
              />
              Team Members
            </Link>
          </li>
        </ul>
      </div>
      <div className="pb-5 mb-5 border-b border-solid border-[#EBEFF3]">
        <h6 className="text-xs font-bold leading-[160%] uppercase text-[#485868] mb-[14px]">
          system settings
        </h6>
        <ul>
          <li>
            <Link
              className={`text-base font-bold leading-[160%] text-customGray flex items-center gap-2.5 mb-[14px] ${
                activeLink === "/admin-dashboard/settings/notification-settings"
                  ? "text-primary"
                  : ""
              }`}
              onClick={handleSidebarTabs}
              to="/admin-dashboard/settings/notification-settings"
            >
              <BellIcon
                className={`w-6 h-6 text-customGray ${
                  activeLink ===
                  "/admin-dashboard/settings/notification-settings"
                    ? "text-primary"
                    : ""
                }`}
              />
              Notifications
            </Link>
          </li>
          <li>
            <Link
              className={`text-base font-bold leading-[160%] text-customGray flex items-center gap-2.5 mb-[14px] ${
                activeLink === "/admin-dashboard/settings/password-settings"
                  ? "text-primary"
                  : ""
              }`}
              onClick={handleSidebarTabs}
              to="/admin-dashboard/settings/password-settings"
            >
              <LockClosedIcon
                className={`w-6 h-6 ${
                  activeLink === "/admin-dashboard/settings/password-settings"
                    ? "text-primary"
                    : "text-customGray"
                }`}
              />
              Password
            </Link>
          </li>
          <li>
            <Link
              className={`text-base font-bold leading-[160%] text-customGray flex items-center gap-2.5 ${
                activeLink === "/admin-dashboard/settings/security-settings"
                  ? "text-primary"
                  : ""
              }`}
              onClick={handleSidebarTabs}
              to="/admin-dashboard/settings/security-settings"
            >
              <ShieldCheckIcon
                className={`w-6 h-6  ${
                  activeLink === "/admin-dashboard/settings/security-settings"
                    ? "text-primary"
                    : "text-customGray"
                }`}
              />
              Security
            </Link>
          </li>
        </ul>
      </div>
      <Link
        className="text-base font-bold leading-[160%] text-[#EF2121] flex items-center gap-2.5"
        onClick={() => {
          localStorage.removeItem("token");
          localStorage.removeItem("isUpdateInsurancePer")
          //   localStorage.removeItem("smsTemplate");
          window.location.replace("/signin");
        }}
      >
        <DocumentTextIcon className="w-6 h-6 text-[#EF2121]" />
        Log Out
      </Link>
    </div>
  );
};

export default PersonalAdminAsideBar;
